import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import CloseIcon from '@mui/icons-material/Close';
import { PersonalDataModal } from 'components/FeedbackModal/InfoModal';
import { BackdropLoad } from 'components/UI/Backdrop';
import { InfoModal } from 'components/UI/InfoModal';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { getText } from 'services/languageService';
import {
  resetFeedbackAction,
  setConfirmFeedbackAction,
  setFeedbackMailAction,
  setFeedbackMessageAction,
  setFeedbackNameAction,
  setOpenFeedbackInfoModalAction,
  setOpenFeedbackModalAction,
  setOpenFeedbackWarningModalAction,
  submitFeedbackFormAction,
} from 'store/actions/feedback';

export const FeedbackModal: React.FC = () => {
  const {
    isOpenModal: open,
    name,
    mail,
    message,
    confirm,
    isOpenInfoModal,
    openWarningModal,
  } = useAppSelector((state) => state.feedback);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const [nameError, setNameError] = useState(false);
  const [mailError, setMailError] = useState('');
  const [msgError, setMsgError] = useState(false);
  const [load, setLoad] = useState(false);

  const handleClose = () => {
    dispatch(setOpenFeedbackModalAction(false));
  };

  const onChangeName = (event) => {
    dispatch(setFeedbackNameAction(event.target.value));
    if (nameError) setNameError(false);
  };

  const onChangeMail = (event) => {
    dispatch(setFeedbackMailAction(event.target.value));
    if (mailError) setMailError('');
  };

  const onChangeMessage = (event) => {
    dispatch(setFeedbackMessageAction(event.target.value));
    if (msgError) setMsgError(false);
  };

  const onConfirm = () => {
    dispatch(setConfirmFeedbackAction(!confirm));
  };

  const toggleInfoModal = () => {
    dispatch(setOpenFeedbackInfoModalAction(!isOpenInfoModal));
  };

  const toggleOpenWarningModal = () => {
    dispatch(setOpenFeedbackWarningModalAction(!openWarningModal));
  };

  const validate = () => {
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

    if (!name.trim()) setNameError(true);
    if (!mail.trim()) setMailError(getText('REQUIRED_FEEDBACK_EMAIL'));
    if (!emailPattern.test(mail)) setMailError(getText('WRONG_FEEDBACK_EMAIL'));
    if (!message.trim()) setMsgError(true);
    if (!confirm) toggleOpenWarningModal();

    return (
      name.trim() &&
      mail.trim() &&
      message.trim() &&
      emailPattern.test(mail) &&
      confirm
    );
  };

  const sendFeedback = () => {
    const isValid = validate();

    if (isValid) {
      setLoad(true);
      setTimeout(() => {
        setLoad(false);
        dispatch(submitFeedbackFormAction(name, mail, message));
        handleClose();
      }, 1000);
    }
  };

  useEffect(() => {
    if (!open) {
      dispatch(resetFeedbackAction());
      setMailError('');
      setMsgError(false);
      setNameError(false);
    }
  }, [open]);

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth={true}
      open={open}
      onClose={handleClose}
      maxWidth="xs"
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', p: 2, pb: 0 }}
      >
        <Typography
          sx={{
            color: (theme: Theme) => theme.palette.primary.main,
            fontSize: 15,
            pl: 1,
          }}
        >
          {getText('FEEDBACK')}
        </Typography>
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 1.5, pb: 0 }}>
        <Box sx={{ display: 'flex', mb: 1, pt: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={name}
            label={getText('YOUR_NAME')}
            onChange={onChangeName}
            autoComplete="off"
            error={nameError}
          />
          <Typography sx={{ color: 'red', ml: 0.5 }}>*</Typography>
        </Box>
        <Box sx={{ display: 'flex', mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={mail}
            onChange={onChangeMail}
            label={getText('YOUR_MAIL')}
            autoComplete="off"
            error={!!mailError}
            helperText={mailError}
          />
          <Typography sx={{ color: 'red', ml: 0.5 }}>*</Typography>
        </Box>
        <Box sx={{ display: 'flex', mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={message}
            onChange={onChangeMessage}
            rows={5}
            multiline
            label={getText('YOUR_MESSAGE')}
            autoComplete="off"
            error={msgError}
          />
          <Typography sx={{ color: 'red', ml: 0.5 }}>*</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Checkbox checked={confirm} onClick={onConfirm} />
          {confirm ? (
            <Typography>{getText('FEEDBACK_CONFIRMED')}</Typography>
          ) : (
            <Typography>
              {getText('FEEDBACK_CONFIRM_DEFAULT')}{' '}
              <span
                style={{
                  color: ((theme) => theme.palette.primary.main)(theme),
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={toggleInfoModal}
              >
                {getText('FEEDBACK_CONFIRM_SPAN')}
              </span>
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          p: 1,
          pb: 2,
        }}
      >
        <Button
          autoFocus
          onClick={sendFeedback}
          variant="contained"
          sx={{ minWidth: '110px' }}
        >
          {getText('SEND')}
        </Button>
        <Button
          onClick={handleClose}
          autoFocus
          variant="contained"
          sx={{ minWidth: '110px' }}
        >
          {getText('CANCEL')}
        </Button>
      </DialogActions>
      <PersonalDataModal handleClose={toggleInfoModal} fs={fullScreen} />
      <InfoModal
        centeredText
        isOpen={openWarningModal}
        handleClose={toggleOpenWarningModal}
        modalText={getText('FEEDBACK_WARNING_TEXT')}
      />
      <BackdropLoad isOpen={load} />
    </Dialog>
  );
};
